/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: fix any
// FIXME: remove replace list-tags with labels
import { useStackNavigate } from '@/app/navigation/stack-links';
import {
  ColumnConfig,
  completeConfig,
} from '@/components/ui/data-table/config';
import { DataTable } from '@/components/ui/data-table/data-table';
import { DataTableConsumer } from '@/components/ui/data-table/types';
import type { TokenDefinition } from '@/components/ui/TokenizedInput/types';
import { useFilterableTable } from '@/hooks/useFilterableTable';
import { usePanelFilterState } from '@/hooks/usePanelFilterState';
import { usePanelSelectionState } from '@/hooks/usePanelSelectionState';
import { usePersistedTableConfig } from '@/hooks/usePersistedTableConfig';
import { ListSummary } from '@certhon/models';
import { ColumnDef, RowData } from '@tanstack/react-table';
import { Tag as TagIcon } from 'lucide-react';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { ZodType } from 'zod';
import { TagKeyOptions } from './ListTagsField';
import { TagsCell } from './TagsCell';

// Build flat `key:value` token options from a tag key map so the filter picker
// mirrors the form's tag picker.
export function buildTagTokenOptions(
  tagKeys: TagKeyOptions,
): { value: string; label: string }[] {
  return Object.entries(tagKeys).flatMap(([key, { values }]) =>
    values.map((value) => ({
      value: `${key}:${value}`,
      label: `${key}: ${value}`,
    })),
  );
}

export interface EntityListTableConfig<T extends RowData> {
  /** Persisted layout config namespace + filter/selection state prefix base. */
  tableId: string;
  /** Field schema map driving filter tokens + auto columns. */
  fields: Record<string, ZodType>;
  /** Default column config (used when user has no persisted overrides). */
  defaultColumnConfig: ColumnConfig[];
  /** Tag key map driving the Tags column + filter token options. */
  tagKeys: TagKeyOptions;
  /** Extra columns appended to the auto-generated ones (e.g. feedback). */
  extraColumns?: ColumnDef<T>[];
  /** Initial filter applied when no persisted filter exists. */
  defaultFilter?: string;
  /** Prefix for panel filter + selection state keys. */
  statePrefix: string;
  /** Build href from row. */
  rowToHref: (row: T) => string;
  /** Path to navigate to on "new". */
  addPath: string;
  /** Row actions menu builder (depends on caller context like current user). */
  useRowActions: () => any[];
  /** Entity label used by the DataTable (pluralised). */
  entityName: string;
}

interface EntityListTableProps<T extends RowData> extends DataTableConsumer<T> {
  config: EntityListTableConfig<T>;
}

export function EntityListTable<T extends ListSummary>({
  config,
  data,
  isLoading,
}: EntityListTableProps<T>): ReactNode {
  const {
    tableId,
    fields,
    defaultColumnConfig,
    tagKeys,
    extraColumns,
    defaultFilter,
    statePrefix,
    rowToHref,
    addPath,
    useRowActions,
    entityName,
  } = config;

  const { filterValue, setFilterValue } = usePanelFilterState({
    prefix: statePrefix,
    defaultFilter,
  });

  const { rowSelection, setRowSelection, clearSelection } =
    usePanelSelectionState({ prefix: statePrefix });

  // Clear selection when filter changes
  const prevFilterRef = useRef(filterValue);
  useEffect(() => {
    if (prevFilterRef.current !== filterValue) {
      clearSelection();
      prevFilterRef.current = filterValue;
    }
  }, [filterValue, clearSelection]);

  const rowActions = useRowActions();

  const tagTokenOptions = useMemo(
    () => buildTagTokenOptions(tagKeys),
    [tagKeys],
  );

  const customColumns = useMemo<ColumnDef<T>[]>(
    () => [
      {
        id: 'tags',
        header: 'Tags',
        accessorKey: 'tags',
        cell: ({ getValue }) => (
          <TagsCell
            tags={getValue() as string[] | undefined}
            options={tagKeys}
          />
        ),
      },
      ...(extraColumns ?? []),
    ],
    [tagKeys, extraColumns],
  );

  const customTokens = useMemo<TokenDefinition[]>(
    () => [
      {
        key: 'tags',
        label: 'Tags',
        type: 'select',
        icon: TagIcon,
        operations: ['is', 'is_not'],
        options: tagTokenOptions,
      },
    ],
    [tagTokenOptions],
  );

  const emptyArray = useMemo<T[]>(() => [], []);

  const {
    tokenDefinitions,
    filteredData,
    columns,
    isFiltering,
    resultCount,
    totalCount,
  } = useFilterableTable<T>({
    fields,
    data: data || emptyArray,
    filterValue,
    customColumns,
    customTokens,
  });

  const completeDefaultConfig = useMemo(
    () => completeConfig(defaultColumnConfig, columns),
    [defaultColumnConfig, columns],
  );

  const [tableConfig, setTableConfig] = usePersistedTableConfig(
    tableId,
    completeDefaultConfig,
  );

  const navigate = useStackNavigate();

  const createUriPath = useCallback(
    (row: T): string => rowToHref(row),
    [rowToHref],
  );

  const handleNew = useCallback(() => {
    navigate({ to: addPath });
  }, [navigate, addPath]);

  return (
    <DataTable<T, any>
      isLoading={isLoading}
      data={filteredData}
      columns={columns}
      config={tableConfig}
      onConfigChange={setTableConfig}
      createUriPath={createUriPath}
      onNew={handleNew}
      filterValue={filterValue}
      onFilterChange={setFilterValue}
      tokenDefinitions={tokenDefinitions}
      isFiltering={isFiltering}
      resultCount={resultCount}
      totalCount={totalCount}
      entityName={entityName}
      addPath={addPath}
      actions={rowActions}
      rowSelection={rowSelection}
      onRowSelectionChange={setRowSelection}
      getRowId={(row) => (row as ListSummary).id}
    />
  );
}
